import { Component } from '@theme/component';

const configurations = new Map();
const choices = new Map();

function getConfiguration(url) {
  if (!configurations.has(url)) {
    configurations.set(url, fetch(url).then(response => {
      if (!response.ok) throw new Error('configuration');
      return response.json();
    }).catch(error => {
      configurations.delete(url);
      throw error;
    }));
  }
  return configurations.get(url);
}

class GetYourHat extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.hatRequest?.abort();
  }

  async initialize() {
    this.controller?.abort();
    this.hatRequest?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.design = this.querySelector('[data-hat-design]');
    this.colour = this.querySelector('[data-hat-colour]');
    this.search = this.querySelector('[data-hat-search]');
    this.status = this.querySelector('[data-hat-status]');
    this.button = this.querySelector('[data-hat-add]');
    this.image = this.querySelector('[data-hat-image]');
    this.selectedLabel = this.querySelector('[data-hat-selected]');
    this.button.disabled = true;
    this.hat = null;
    this.busy = false;
    this.variants = JSON.parse(this.querySelector('[data-garment-variants]').textContent);
    const options = { signal: controller.signal };
    this.design.addEventListener('change', () => this.loadBeanie(), options);
    this.colour.addEventListener('change', () => this.rememberChoice(), options);
    this.search.addEventListener('input', () => this.filterDesigns(), options);
    this.button.addEventListener('click', () => this.addPair(), options);
    try {
      this.config = await getConfiguration(this.dataset.configUrl);
      if (controller.signal.aborted) return;
      this.garment = this.config.garments[this.dataset.productId];
      if (!this.garment || this.config.currency !== this.dataset.currency) throw new Error('eligibility');
      this.design.replaceChildren(...this.config.beanies.map(hat => new Option(hat.label || hat.title, hat.id)));
      const remembered = choices.get(this.dataset.productId);
      const desired = remembered?.design || this.garment.recommendedBeanie;
      if ([...this.design.options].some(option => option.value === desired)) this.design.value = desired;
      this.design.disabled = false;
      this.search.disabled = false;
      this.status.textContent = '';
      await this.loadBeanie(remembered?.colour);
    } catch (error) {
      if (!controller.signal.aborted) this.status.textContent = this.dataset.error;
    }
  }

  filterDesigns() {
    const current = this.design.value;
    const term = this.search.value.trim().toLocaleLowerCase();
    const hats = this.config.beanies.filter(hat => hat.title.toLocaleLowerCase().includes(term));
    this.design.replaceChildren(...hats.map(hat => new Option(hat.label || hat.title, hat.id)));
    if (hats.some(hat => hat.id === current)) this.design.value = current;
    this.design.disabled = hats.length === 0;
    if (!hats.length) {
      this.hatRequest?.abort();
      this.hat = null;
      this.colour.replaceChildren();
      this.colour.disabled = true;
      this.button.disabled = true;
      this.image.hidden = true;
      this.selectedLabel.textContent = '';
      this.status.textContent = this.dataset.noResults;
      return;
    }
    this.loadBeanie();
  }

  async loadBeanie(preferredColour) {
    this.hatRequest?.abort();
    const request = new AbortController();
    this.hatRequest = request;
    this.button.disabled = true;
    this.colour.disabled = true;
    this.hat = null;
    const approved = this.config.beanies.find(hat => hat.id === this.design.value);
    if (!approved) return;
    this.image.hidden = true;
    this.selectedLabel.textContent = '';
    this.status.textContent = this.dataset.loading;
    try {
      const url = new URL(`${this.dataset.rootUrl}products/${approved.handle}.js`, window.location.origin);
      const response = await fetch(url, { signal: request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('availability');
      const product = await response.json();
      if (request.signal.aborted || this.controller.signal.aborted || approved.id !== this.design.value) return;
      const ids = new Set(approved.variants.map(variant => variant.id));
      const variants = product.variants.filter(variant => ids.has(String(variant.id)) && variant.available && variant.price === 1500);
      if (!variants.length) throw new Error('availability');
      this.hat = { ...approved, variants, image: product.featured_image };
      this.colour.replaceChildren(...variants.map(variant => new Option(variant.title, String(variant.id))));
      const preferred = variants.find(variant => String(variant.id) === preferredColour);
      if (preferred) this.colour.value = String(preferred.id);
      this.colour.disabled = false;
      this.button.disabled = false;
      this.status.textContent = '';
      this.rememberChoice();
    } catch (error) {
      if (error.name !== 'AbortError') this.status.textContent = this.dataset.unavailable;
    }
  }

  rememberChoice() {
    const variant = this.hat?.variants.find(item => String(item.id) === this.colour.value);
    if (!variant) return;
    choices.set(this.dataset.productId, { design: this.design.value, colour: this.colour.value });
    const recommended = this.design.value === this.garment.recommendedBeanie;
    this.selectedLabel.textContent = `${this.hat.title} — ${variant.title}${recommended ? ` (${this.dataset.recommended})` : ''}`;
    const image = variant.featured_image?.src || this.hat.image;
    if (image) {
      this.image.src = new URL(image, window.location.origin).href;
      this.image.alt = `${this.hat.title} — ${variant.title}`;
      this.image.hidden = false;
    } else this.image.hidden = true;
  }

  selectedGarment(form) {
    const section = form.closest('section');
    const selected = [...(section?.querySelectorAll('input[data-option-name]:checked') || [])];
    const options = new Map(selected.map(input => [Number(input.dataset.fieldsetIndex), input.value]));
    if (options.size) return this.variants.find(variant => variant.options.length === options.size && variant.options.every((value, index) => options.get(index) === value));
    return this.variants.find(variant => String(variant.id) === String(new FormData(form).get('id')));
  }

  async addPair() {
    if (this.busy || !this.hat || !this.garment) return;
    const form = document.getElementById(this.dataset.formId);
    if (!form || !form.reportValidity()) return;
    const data = new FormData(form);
    const variant = this.selectedGarment(form);
    const quantity = Number(data.get('quantity') || 1);
    const rule = variant?.quantity_rule || { min: 1, increment: 1 };
    if (!variant?.available || variant.price !== this.garment.expectedPrice || !Number.isSafeInteger(quantity) || quantity < (rule.min || 1) || (rule.max && quantity > rule.max) || (quantity - (rule.min || 1)) % (rule.increment || 1) !== 0) {
      this.status.textContent = this.dataset.error;
      return;
    }
    const hatVariantId = this.colour.value;
    const chosenHat = this.hat;
    this.busy = true;
    this.button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = this.dataset.busy;
    try {
      const response = await fetch(new URL(`${this.dataset.rootUrl}products/${chosenHat.handle}.js`, window.location.origin), { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(this.dataset.unavailable);
      const current = await response.json();
      const hatVariant = current.variants.find(item => String(item.id) === hatVariantId);
      if (!hatVariant?.available || hatVariant.price !== 1500 || !chosenHat.variants.some(item => String(item.id) === hatVariantId)) throw new Error(this.dataset.unavailable);
      const properties = {};
      for (const [key, value] of data.entries()) {
        const match = key.match(/^properties\[(.*)\]$/);
        if (match && typeof value === 'string') properties[match[1]] = value;
      }
      const added = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: String(variant.id), quantity, properties }, { id: hatVariantId, quantity }] })
      });
      const result = await added.json();
      if (!added.ok) throw new Error(result.description || this.dataset.error);
      window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      this.status.textContent = `${error.message || this.dataset.error} ${this.dataset.checkCart}`;
      this.busy = false;
      this.button.disabled = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('get-your-hat')) customElements.define('get-your-hat', GetYourHat);
